import { useEffect, useState, useRef } from 'react'
import { Canvas, FabricImage } from 'fabric'
import { loadAndPositionMedia, MediaLoadError } from '../utils/mediaUtils'
import { updateCanvasDimensions } from '../utils/canvasUtils'
import { calculateCanvasDimensions } from '../../utils/canvas/canvasDimensions'
import type { MediaDimensions, CanvasScale } from '../types'

/**
 * Параметры для хука загрузки медиа
 */
export interface UseMediaLoadingParams {
    imageUrl: string | null
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    imageObjectRef: React.MutableRefObject<FabricImage | null>
    mediaDimensionsRef: React.MutableRefObject<MediaDimensions | null>
    setMediaDimensions: React.Dispatch<
        React.SetStateAction<MediaDimensions | null>
    >
    previousScaleRef: React.MutableRefObject<CanvasScale | null>
    width: number
    height: number
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
    onError?: (error: Error) => void
    onLoad?: () => void
    onScaleChange?: (scale: CanvasScale) => void
    canvasReadyTrigger?: number // Триггер для перезапуска хука при создании canvas
    initialScale?: CanvasScale | null // Начальный масштаб из сохраненных данных
}

/**
 * Хук для загрузки и отображения медиа на canvas
 * Обрабатывает загрузку изображения, расчет масштаба и позиционирование
 */
export function useMediaLoading(params: UseMediaLoadingParams): {
    isLoading: boolean
    error: Error | null
} {
    const {
        imageUrl,
        fabricCanvasRef,
        imageObjectRef,
        mediaDimensionsRef,
        setMediaDimensions,
        previousScaleRef,
        width,
        height,
        safeArea,
        onError,
        onLoad,
        onScaleChange,
        canvasReadyTrigger,
        initialScale,
    } = params

    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const lastImageUrlRef = useRef<string | null>(null)

    useEffect(() => {
        // Ждем, пока canvas будет инициализирован
        if (!fabricCanvasRef.current) {
            setIsLoading(false)
            return
        }

        const fabricCanvas = fabricCanvasRef.current

        // Проверяем, что canvas все еще существует и привязан к DOM
        const canvasElement = fabricCanvas.getElement()
        if (!canvasElement || !canvasElement.parentElement) {
            setIsLoading(false)
            return
        }

        // Если URL не изменился и изображение уже загружено, не загружаем повторно
        if (imageUrl === lastImageUrlRef.current && imageObjectRef.current) {
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        setError(null)

        // Если нет imageUrl, просто показываем пустой canvas
        if (!imageUrl) {
            // Удаляем старое изображение, если оно есть
            if (imageObjectRef.current) {
                fabricCanvas.remove(imageObjectRef.current)
                imageObjectRef.current = null
            }
            fabricCanvas.clear()
            setIsLoading(false)
            mediaDimensionsRef.current = null
            setMediaDimensions(null)
            lastImageUrlRef.current = null
            return
        }

        // Удаляем старое изображение перед загрузкой нового
        if (imageObjectRef.current && lastImageUrlRef.current !== imageUrl) {
            fabricCanvas.remove(imageObjectRef.current)
            imageObjectRef.current = null
        }

        // Сохраняем текущий URL
        lastImageUrlRef.current = imageUrl

        // Флаг для отслеживания, был ли компонент размонтирован
        let isMounted = true

        // Определяем размеры canvas для загрузки медиа
        // Используем сохраненный масштаб, если он есть, иначе текущие размеры canvas
        const canvasWidthForMedia =
            initialScale?.canvasWidth ?? fabricCanvas.width
        const canvasHeightForMedia =
            initialScale?.canvasHeight ?? fabricCanvas.height

        // Если есть сохраненный масштаб, обновляем размеры canvas перед загрузкой медиа
        if (
            initialScale &&
            (fabricCanvas.width !== initialScale.canvasWidth ||
                fabricCanvas.height !== initialScale.canvasHeight)
        ) {
            fabricCanvas.setDimensions({
                width: initialScale.canvasWidth,
                height: initialScale.canvasHeight,
            })
        }

        // Загружаем и размещаем медиа
        loadAndPositionMedia({
            imageUrl,
            fabricCanvas,
            canvasWidth: canvasWidthForMedia,
            canvasHeight: canvasHeightForMedia,
            safeArea,
        })
            .then(({ image, dimensions }) => {
                // Проверяем, что компонент все еще смонтирован и canvas существует
                if (
                    !isMounted ||
                    !fabricCanvasRef.current ||
                    !fabricCanvas.getElement()
                ) {
                    return
                }

                try {
                    // Сохраняем размеры медиа
                    mediaDimensionsRef.current = dimensions
                    setMediaDimensions(dimensions)

                    // Получаем актуальные размеры canvas
                    const canvasElement = fabricCanvas.getElement()
                    if (!canvasElement) {
                        throw new Error('Canvas element not found')
                    }

                    // Определяем финальные размеры canvas
                    // Если есть сохраненный масштаб и он соответствует загруженному медиа,
                    // используем его (размеры уже должны быть применены выше)
                    let finalCanvasWidth = fabricCanvas.width
                    let finalCanvasHeight = fabricCanvas.height

                    // Используем сохраненный масштаб, если он есть и соответствует загруженному медиа
                    if (
                        initialScale &&
                        initialScale.originalWidth === dimensions.width &&
                        initialScale.originalHeight === dimensions.height
                    ) {
                        // Используем сохраненный масштаб (размеры canvas уже должны быть обновлены выше)
                        finalCanvasWidth = initialScale.canvasWidth
                        finalCanvasHeight = initialScale.canvasHeight

                        // Убеждаемся, что размеры canvas соответствуют сохраненному масштабу
                        // (на случай, если они не были обновлены выше)
                        if (
                            fabricCanvas.width !== finalCanvasWidth ||
                            fabricCanvas.height !== finalCanvasHeight
                        ) {
                            fabricCanvas.setDimensions({
                                width: finalCanvasWidth,
                                height: finalCanvasHeight,
                            })
                            // Пересчитываем позицию медиа после изменения размеров canvas
                            image.set({
                                left: finalCanvasWidth / 2,
                                top: finalCanvasHeight / 2,
                            })
                            // Обновляем координаты объекта после изменения позиции
                            image.setCoords()
                            fabricCanvas.renderAll()
                        }

                        // Сохраняем масштаб в ref
                        previousScaleRef.current = initialScale
                    } else {
                        // initialScale не соответствует загруженному медиа (креатив изменился)
                        // или initialScale отсутствует - нужно пересчитать всё заново

                        // Обновляем размеры canvas под новую картинку
                        const containerElement = canvasElement.parentElement
                        if (containerElement) {
                            // Проверяем, что контейнер имеет реальные размеры
                            // Если размеры нулевые, используем минимальные размеры как fallback
                            const containerWidth = containerElement.clientWidth || width
                            const containerHeight = containerElement.clientHeight || height

                            const {
                                width: newCanvasWidth,
                                height: newCanvasHeight,
                            } = calculateCanvasDimensions({
                                mediaWidth: dimensions.width,
                                mediaHeight: dimensions.height,
                                containerWidth,
                                containerHeight,
                                minWidth: width,
                                minHeight: height,
                            })

                            // Обновляем размеры canvas если они изменились
                            if (
                                fabricCanvas.width !== newCanvasWidth ||
                                fabricCanvas.height !== newCanvasHeight
                            ) {
                                fabricCanvas.setDimensions({
                                    width: newCanvasWidth,
                                    height: newCanvasHeight,
                                })
                            }

                            // ВСЕГДА центрируем изображение по новым размерам canvas
                            // т.к. изображение было позиционировано по старым размерам в loadAndPositionMedia
                            image.set({
                                left: newCanvasWidth / 2,
                                top: newCanvasHeight / 2,
                            })
                            image.setCoords()
                            fabricCanvas.renderAll()

                            finalCanvasWidth = newCanvasWidth
                            finalCanvasHeight = newCanvasHeight
                        } else {
                            // Fallback: центрируем по текущим размерам canvas
                            finalCanvasWidth = fabricCanvas.width
                            finalCanvasHeight = fabricCanvas.height
                            image.set({
                                left: finalCanvasWidth / 2,
                                top: finalCanvasHeight / 2,
                            })
                            image.setCoords()
                            fabricCanvas.renderAll()
                        }
                    }

                    // Сохраняем ссылку на изображение
                    imageObjectRef.current = image

                    // Убеждаемся, что медиа правильно позиционировано
                    // (на случай, если размеры canvas изменились после загрузки)
                    image.setCoords()
                    fabricCanvas.renderAll()

                    // Уведомляем о масштабе для сохранения
                    if (onScaleChange) {
                        const newScale: CanvasScale = {
                            canvasWidth: finalCanvasWidth,
                            canvasHeight: finalCanvasHeight,
                            originalWidth: dimensions.width,
                            originalHeight: dimensions.height,
                        }
                        const prevScale = previousScaleRef.current
                        if (
                            !prevScale ||
                            prevScale.canvasWidth !== newScale.canvasWidth ||
                            prevScale.canvasHeight !== newScale.canvasHeight ||
                            prevScale.originalWidth !==
                                newScale.originalWidth ||
                            prevScale.originalHeight !== newScale.originalHeight
                        ) {
                            previousScaleRef.current = newScale
                            onScaleChange(newScale)
                        }
                    }

                    // Используем requestAnimationFrame для гарантии, что canvas отрендерен
                    // Это предотвращает проблемы с позиционированием при первом открытии
                    requestAnimationFrame(() => {
                        if (!isMounted) {
                            return
                        }

                        // Дополнительная проверка, что canvas все еще существует
                        if (
                            !fabricCanvasRef.current ||
                            !fabricCanvas.getElement() ||
                            !fabricCanvas.getElement().parentElement
                        ) {
                            return
                        }

                        // Финальный пересчёт позиции медиа после полного рендера
                        // Это важно для новых картинок, когда контейнер мог изменить размеры
                        const currentCanvasWidth = fabricCanvas.width
                        const currentCanvasHeight = fabricCanvas.height
                        const currentLeft = image.left
                        const currentTop = image.top
                        const expectedLeft = currentCanvasWidth / 2
                        const expectedTop = currentCanvasHeight / 2

                        // Если позиция не соответствует центру canvas, корректируем
                        if (
                            Math.abs((currentLeft ?? 0) - expectedLeft) > 1 ||
                            Math.abs((currentTop ?? 0) - expectedTop) > 1
                        ) {
                            image.set({
                                left: expectedLeft,
                                top: expectedTop,
                            })
                            image.setCoords()
                            fabricCanvas.renderAll()
                        }

                        setIsLoading(false)
                        onLoad?.()
                    })
                } catch (err: unknown) {
                    if (!isMounted) {
                        return
                    }

                    // Если это MediaLoadError, используем его напрямую
                    // Иначе оборачиваем в обычный Error
                    const error =
                        err instanceof MediaLoadError
                            ? err
                            : err instanceof Error
                              ? err
                              : new Error('Не удалось обработать изображение')

                    setError(error)
                    setIsLoading(false)
                    onError?.(error)
                }
            })
            .catch((err: unknown) => {
                if (!isMounted) {
                    return
                }

                // Если это MediaLoadError, используем его напрямую
                // Иначе оборачиваем в обычный Error
                const error =
                    err instanceof MediaLoadError
                        ? err
                        : err instanceof Error
                          ? err
                          : new Error('Не удалось загрузить медиа')

                setError(error)
                setIsLoading(false)
                onError?.(error)
            })

        // Cleanup функция
        return () => {
            isMounted = false
            // Отменяем загрузку, если компонент размонтирован
            // (Promise уже запущен, но мы проверим isMounted в then/catch)
        }
    }, [
        imageUrl,
        width,
        height,
        safeArea.top,
        safeArea.bottom,
        safeArea.left,
        safeArea.right,
        canvasReadyTrigger,
        initialScale,
        // Не включаем refs и функции в зависимости, так как они стабильны
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ])

    return { isLoading, error }
}
